/* ============
 * Edit Page
 * ============
 *
 * This is the page for editing a product group.
 */
import { mapGetters, mapMutations } from 'vuex';
import recipeIngredienceTransformer from '../../../../transformers/recipeIngredience';
import recipeProcedureTransformer from '../../../../transformers/recipeProcedure';
import batchIngredienceTransformer from '../../../../transformers/batchIngredience';
import productService from './../../../../services/product';
import batchService from '../../../../services/batch';
import materialService from './../../../../services/material';
import batchIngredienceService from './../../../../services/batchIngredience';
import store from '../../../../store';
import transferService from './../../../../services/transfer';
import batchLogService from './../../../../services/batchLog';
import debounce from 'debounce';
import addBatchLog from './../../../../components/addBatchLog/addBatchLog.vue';
import batchStartPopup from './../../../../components/batchStartPopup/batchStartPopup.vue';
import Forms from './../../../../utils/forms/forms';
import bulkAddProductModal from './../../../../components/bulkAddProductModal/bulkAddProductModal.vue';
import batchTransformer from './../../../../transformers/batch';
import BridgeHandoffMixin from './../../../../mixins/bridgeHandoff';

export default {
  data() {
    return {
      batch: {},
      loading: false,
      form: new Forms({
        id: {
          value: '',
          type: 'text',
        },
        transferDate: {
          value: '',
          type: 'plaintext',
        },
        productName: {
          value: '',
          type: 'autocomplete',
          attributes: {
            disabled: true,
            href: 'products/_/edit#productInfo',
            targetId: 'productId', // specify targetId if you don't want to use `id` for href, and make sure your targetId exist.
          },
        },
        qty: {
          value: 0,
          type: 'number',
        },
        warehouse: {
          value: '',
          type: 'plaintext',
        },
      }),
      formIngredience: new Forms({
        batchId: {
          value: '',
          type: 'text',
        },
        recipeId: {
          value: '',
          type: 'text',
        },
        materialId: {
          value: '',
          type: 'select',
        },
        unitMeasure: {
          value: '',
          type: 'text',
        },
        amount: {
          value: '',
          type: 'text',
        },
        deductedAmount: {
          value: '',
          type: 'number',
        },
      }),
      formBatchEdit: new Forms({
        batchNumber: {
          value: '',
          type: 'number',
        },
        plannedStartingDate: {
          type: 'date',
          value: '',
        },
        bestBeforeDate: {
          type: 'date',
          value: '',
        },
        recipeId: {
          value: '',
          type: 'hidden',
        },
        status: {
          value: '',
          type: 'hidden',
        },
      }),
      hiddenFields: ['batchId', 'recipeId'],
      additionalButtons: [
        {
          text: this.$t('static.recipeIngrediences.index.addIngrediences'),
          action: this.showBulkAddIngredienceModal,
        },
        {
          text: this.$t(
            'static.batches.view.deductIngredience.deductAll.button'
          ),
          action: this.deductAll(),
        },
      ],
      tabIndex: '1',
      materialsLoading: false,
      startBatchloading: false,
      completeBatchloading: false,
    };
  },
  created() {
    this.show();
  },

  computed: {
    ...mapGetters({
      allMaterials: 'allMaterials',
      recipes: 'allRecipes',
      transfers: 'allTransfers',
      logs: 'paginatedBatchLogs',
      logQuery: 'batchLogQuery',
      ingrediences: 'allBatchIngrediences',
    }),
    upcomingProcedures() {
      if (this.batch.recipe && this.batch.recipe.procedures) {
        return this.batch.recipe.procedures.filter(p => !p.status).map(procedure =>
          Object.assign({},
            recipeProcedureTransformer.fetch(procedure), {
              ingrediences: recipeIngredienceTransformer.fetchCollection(procedure.ingrediences),
            },
          ));
      }
      return [];
    },
    ongoingProcedures() {
      if (this.batch.recipe && this.batch.recipe.procedures) {
        return this.batch.recipe.procedures.filter(p => p.status === 'started').map(procedure =>
          Object.assign({},
            recipeProcedureTransformer.fetch(procedure), {
              ingrediences: recipeIngredienceTransformer.fetchCollection(procedure.ingrediences),
            },
          ));
      }
      return [];
    },
    completedProcedures() {
      if (this.batch.recipe && this.batch.recipe.procedures) {
        return this.batch.recipe.procedures.filter(p => p.status === 'completed').map(procedure =>
          Object.assign({},
            recipeProcedureTransformer.fetch(procedure), {
              ingrediences: recipeIngredienceTransformer.fetchCollection(procedure.ingrediences),
            },
          ));
      }
      return [];
    },
    tranferProducts() {
      const products = [];
      this.transfers.items.forEach((transfer) => {
        if (transfer.products) {
          transfer.products.forEach((tranferProduct) => {
            products.push(Object.assign({}, {
              name: tranferProduct.productName,
              productId: tranferProduct.productId,
              transferDate: transfer.date,
              warehouse: transfer.toWarehouseName,
            }, tranferProduct));
          });
        }
      });
      return products;
    },
    ingredienceList() {
      return this.ingrediences.map(ingredience => Object.assign({}, ingredience));
    },
    disabledItems() {
      return this.ingrediences.filter(item => item.deductedAmount >= item.amount).map(ingredience => ingredience.id);
    },
    allIngredienceDeducted() {
      return this.ingrediences.filter(item => item.deductedAmount < item.amount).length === 0;
    },
    disableForm() {
      let disabled = false;
      if (this.batch.ingredienceDeductionType === 'all' && (this.batch.status === 'planned' || this.batch.status === 'started')) {
        disabled = true;
      }
      if (this.allIngredienceDeducted) {
        disabled = true;
      }
      return disabled;
    },
  },

  mixins: [
    BridgeHandoffMixin,
  ],

  methods: {
    ...mapMutations({
      toggleModal: 'TOGGLE_MODAL',
      setModalContent: 'SET_MODAL_CONTENT',
      unsetModalContent: 'UNSET_MODAL_CONTENT',
      setModalSize: 'SET_MODAL_SIZE',
      setModalProps: 'SET_MODAL_PROPS',
      setModalButtonLoading: 'SET_MODAL_BUTTON_LOADING',
    }),
    showBulkAddIngredienceModal() {
      this.setModalContent(() => bulkAddProductModal);
      this.setModalProps({
        fetchProductsMethod: this.fetchAllMaterialsBasic,
        onSave: this.handleBulkIngrediencesSave,
      });
      this.toggleModal(true);
    },

    update() {
      const option = Object.assign(this.form.data());
      batchService.updateBatchTransfer(option.id, { qty: option.qty })
      .then(() => {
        transferService.all({ batch_id: store.state.route.params.id, transfer_history: true })
        .then(() => {
          this.$message({
            showClose: true,
            message: 'Successfully Update',
            type: 'success',
          });
        });
      });
      return Promise.all([]);
    },

    fetchAllMaterialsBasic() {
      return Promise.all([
        productService.allBasic({
          filter: 'active',
          type: 'material',
        }),
      ]);
    },

    handleBulkIngrediencesSave(selectedProducts) {
      const products = selectedProducts.map(p => ({
        materialId: p.id,
        amount: p.qty,
      }));
      this.formIngredience.loading = true;
      return batchIngredienceService.bulkStore(this.batch.id, products)
      .then(() => {
        this.formIngredience.loading = false;
      });
    },
    /**
     * Fetches the product group object for editing.
     */
    show() {
      this.loading = true;
      this.formIngredience.disableField('unitMeasure');
      this.formIngredience.disableField('deductedAmount');
      this.formBatchEdit.hideField('plannedStartingDate');
      Promise.all([
        batchService.show(store.state.route.params.id),
        transferService.all({ batch_id: store.state.route.params.id, transfer_history: true }),
        materialService.all(),
      ]).then((data) => {
        if (data[0].ingrediences) {
          const batchIngrediences = batchIngredienceTransformer.fetchCollection(data[0].ingrediences);
          store.dispatch('fetchAllBatchIngrediences', batchIngrediences);
        }

        this.loading = false;
        this.batch = data[0];
        this.formBatchEdit.assignData(
          Object.assign({}, this.batch)
        );

        if (this.batch.startDate === '' && this.batch.status === 'planned') this.formBatchEdit.showField('plannedStartingDate');
        
      });
      this.fetchBatchLogs(this.logQuery.page, this.logQuery.limit);
    },
    /**
     * Fetches paginated batch logs
     *
     * @param  {int} page The page number
     */
    fetchBatchLogs: debounce(function (page, limit) {
      this.loading = true;
      const query = {
        page,
        limit,
        batch_id: store.state.route.params.id,
      };
      store.dispatch('setBatchLogQuery', query);
      this.search = this.logQuery.search;
      batchLogService.items({
        page,
        limit,
        batch_id: store.state.route.params.id,
      })
        .then(() => {
          this.loading = false;
        }).catch(() => {
          this.loading = false;
        });
    }, 800),

    addBatchLog() {
      this.setModalContent(() => addBatchLog);
      this.setModalSize('full');
      this.toggleModal(true);
      this.setModalProps({
        batchId: +store.state.route.params.id,
        page: this.logQuery.page,
        limit: this.logQuery.limit,
      });
    },
    confirmProcedureStart(recipeProcedureId, status) {
      this.$confirm(this.$t('static.batches.view.startProcedure.confirm'), this.$t('static.batches.view.startBatch.title'), {
        confirmButtonText: this.$t('static.batches.view.deductIngredience.confirmButton'),
        cancelButtonText: this.$t('static.batches.view.deductIngredience.cancelButton'),
        distinguishCancelAndClose: true,
        type: 'warning',
      }).then(() => {
        this.setProcedureStatus(recipeProcedureId, status, 'all');
      }).catch((action) => {
        if (action === 'cancel') {
          this.setProcedureStatus(recipeProcedureId, status, 'by_procedure');
        }
      });
    },
    setProcedureStatus(recipeProcedureId, status, deductIngredience) {
      this.loading = true;
      const data = {
        procedure_id: recipeProcedureId,
        ingredience_deduction_type: deductIngredience,
        status,
      };
      batchService.setProcedureStatus(this.batch.id, data)
        .then((response) => {
          this.loading = false;
          this.batch = response;
          this.fetchBatchLogs(this.logQuery.page, this.logQuery.limit);
        }).catch((errors) => {
          this.loading = false;
          if (errors.data.not_enough_stock_materials) {
            this.showStartBatchNotEnoughStockPopup(errors.data.not_enough_stock_materials);
          }
        });
    },

    startBatch(status) {
      this.formBatchEdit.hideField('plannedStartingDate');
      this.startBatchloading = true;
      this.$confirm(this.$t('static.batches.view.startBatch.confirm'), this.$t('static.batches.view.startBatch.title'), {
        confirmButtonText: this.$t('static.batches.view.deductIngredience.confirmButton'),
        cancelButtonText: this.$t('static.batches.view.deductIngredience.cancelButton'),
        distinguishCancelAndClose: true,
        type: 'warning',
      }).then(() => {
        batchService.startBatch(this.batch.id, {
          status,
          ingredience_deduction_type: 'all',
        })
          .then((data) => {
            this.batch = data;
            this.startBatchloading = false;
            this.fetchBatchLogs(this.logQuery.page, this.logQuery.limit);
          }).catch((errors) => {
            if (errors.data.not_enough_stock_materials) {
              this.showStartBatchNotEnoughStockPopup(errors.data.not_enough_stock_materials);
            }
            this.startBatchloading = false;
          });
      }).catch((action) => {
        if (action === 'cancel') {
          batchService.startBatch(this.batch.id, {
            status,
            ingredience_deduction_type: 'by_procedure',
          })
            .then((data) => {
              this.batch = data;
              this.startBatchloading = false;
              this.fetchBatchLogs(this.logQuery.page, this.logQuery.limit);
            }).catch(() => {
              this.startBatchloading = false;
            });
        }
      });
    },
    showStartBatchNotEnoughStockPopup(materials) {
      this.setModalContent(() => batchStartPopup);
      this.setModalSize('full');
      this.toggleModal(true);
      this.setModalProps({
        materials,
        batchId: +this.batch.id,
        message: this.$t('static.batches.view.startBatch.stockError'),
        buttons: [{
          name: this.$t('static.batches.view.startBatch.stockErrorOptions.adjustQty'),
          action: this.gotoMaterialTab,
          title: this.$t('static.batches.view.startBatch.stockErrorOptions.adjustQty'),
        },
        {
          name: this.$t('static.batches.view.startBatch.stockErrorOptions.addNew'),
          action: this.gotoMaterialTab,
          title: this.$t('static.batches.view.startBatch.stockErrorOptions.addNew'),
        },
        {
          name: this.$t('static.batches.view.startBatch.stockErrorOptions.purchaseOrder'),
          action: this.gotoPurchaseOrder,
          title: this.$t('static.batches.view.startBatch.stockErrorOptions.purchaseOrder'),
        },
        {
          name: this.$t('static.batches.view.startBatch.stockErrorOptions.stockAdjustment'),
          action: this.gotoStockAdjustment,
          title: this.$t('static.batches.view.startBatch.stockErrorOptions.stockAdjustment'),
        },
        ],
      });
    },

    completeBatch() {
      this.$confirm(this.$t('static.batches.view.completeBatch.confirm'), this.$t('static.batches.view.completeBatch.title'), {
        confirmButtonText: this.$t('static.batches.view.completeBatch.confirmButton'),
        cancelButtonText: this.$t('button.cancel'),
        distinguishCancelAndClose: true,
        type: 'warning',
      }).then(() => {
        this.completeBatchloading = true;
        batchService.completeBatch(this.batch.id, {
          status,
        })
          .then((data) => {
            this.batch = data;
            this.completeBatchloading = false;
            this.fetchBatchLogs(this.logQuery.page, this.logQuery.limit);
          }).catch((errors) => {
            this.completeBatchloading = false;
            if (errors.data.not_enough_stock_materials) {
              this.showDeductNotEnoughStockPopup(errors.data.not_enough_stock_materials);
            }
            if (errors.data.batch) {
              this.batch = errors.data.batch;
            }
          });
      }).catch(() => {});
    },
    /**
     * Defines the handler adding deductions manually.
     *
     * @param  {Object} item The row item.
     * @param  {String} name The field name.
     * @return {Mixed}      False or the handler function in case of a link needed.
     */
    tableClickHandler(item, name) {
      if (name === 'deductedAmount' && item.deductedAmount < item.amount) {
        const material = this.ingrediences.find(m => m.materialId === item.materialId);
        return () => {
          this.$prompt(`${this.$t('static.batches.view.deductIngredience.text')} "${material.materialName}"`, this.$t('static.batches.view.deductIngredience.title'), {
            confirmButtonText: this.$t('static.batches.view.deductIngredience.confirmButton'),
            cancelButtonText: this.$t('button.cancel'),
            inputValue: +item.amount - +item.deductedAmount,
            inputPattern: /^\$?[0-9]?((\.[0-9]+)|([0-9]+(\.[0-9]+)?))$/,
            inputErrorMessage: this.$t('static.batches.view.deductIngredience.invalidDeductionAmount'),
          }).then(({
            value,
          }) => {
            this.materialsLoading = true;
            batchIngredienceService.deduct(item.id, {
              deduction: value,
            })
              .then(() => {
                this.materialsLoading = false;
              })
              .catch((errors) => {
                this.materialsLoading = false;
                if (errors.data.not_enough_stock_materials) {
                  this.showDeductNotEnoughStockPopup(errors.data.not_enough_stock_materials);
                } else {
                  this.$message({
                    showClose: true,
                    message: this.$t('static.batches.view.deductIngredience.generalError'),
                    type: 'error',
                  });
                }
              });
          }).catch(() => {});
        };
      }

      return false;
    },

    deductAll() {
      return () => {
        this.$confirm(this.$t('static.batches.view.deductIngredience.deductAll.message'), this.$t('static.batches.view.deductIngredience.deductAll.title'), {
          confirmButtonText: this.$t('static.batches.view.deductIngredience.deductAll.confirmButton'),
          type: 'warning',
        }).then(() => {
          this.materialsLoading = true;
          batchService.deductAllIngredience(this.batch.id)
            .then(() => {
              this.materialsLoading = false;
            }).catch((errors) => {
              if (errors.data.not_enough_stock_materials) {
                this.showDeductNotEnoughStockPopup(errors.data.not_enough_stock_materials);
              }
              if (errors.data.batch) {
                this.batch = errors.data.batch;
              }
              this.materialsLoading = false;
            });
        }).catch(() => {});
      };
    },

    showDeductNotEnoughStockPopup(materials) {
      this.setModalContent(() => batchStartPopup);
      this.setModalSize('full');
      this.toggleModal(true);
      this.setModalProps({
        materials,
        batchId: +this.batch.id,
        message: this.$t('static.batches.view.deductIngredience.stockError'),
        buttons: [{
          name: this.$t('static.batches.view.startBatch.stockErrorOptions.adjustQty'),
          action: this.gotoMaterialTab,
          title: this.$t('static.batches.view.startBatch.stockErrorOptions.adjustQty'),
        },
        {
          name: this.$t('static.batches.view.startBatch.stockErrorOptions.purchaseOrder'),
          action: this.gotoPurchaseOrder,
          title: this.$t('static.batches.view.startBatch.stockErrorOptions.purchaseOrder'),
        },
        {
          name: this.$t('static.batches.view.startBatch.stockErrorOptions.stockAdjustment'),
          action: this.gotoStockAdjustment,
          title: this.$t('static.batches.view.startBatch.stockErrorOptions.stockAdjustment'),
        },
        ],
      });
    },

    gotoMaterialTab() {
      this.toggleModal(false);
      this.tabIndex = '2';
    },
    gotoPurchaseOrder() {
      this.toggleModal(false);
      this.$router.push({
        name: 'inventory.purchase.create',
      });
    },
    gotoStockAdjustment() {
      this.toggleModal(false);
      this.$router.push({
        name: 'inventory.adjustment.stock.create',
      });
    },
    camalize(str) {
      return str.toLowerCase().replace(/[^a-zA-Z0-9]+(.)/g, (m, chr) => chr.toUpperCase());
    },

    saveLog() {
      this.formLog.loading = true;
      return batchService.storeLog(this.batch.id, this.formLog.data())
        .then(() => {
          this.formLog.loading = false;
        })
        .catch((errors) => {
          this.formLog.loading = false;
          this.formLog.recordErrors(errors);
          const errorsKeys = Object.keys(this.formLog.errors.errors);
          this.$message({
            showClose: true,
            message: errors[errorsKeys[0]][0],
            type: 'error',
          });
        });
    },

    saveIngredience() {
      this.formIngredience.loading = true;
      const data = Object.assign(this.formIngredience.data(), {
        recipeId: this.batch.recipe.id,
        batchId: this.batch.id,
      });
      return batchIngredienceService.store(data)
        .then(() => {
          this.formIngredience.loading = false;
        })
        .catch((errors) => {
          this.formIngredience.loading = false;
          this.formIngredience.recordErrors(errors);
          const errorsKeys = Object.keys(this.formIngredience.errors.errors);
          this.$message({
            showClose: true,
            message: errors[errorsKeys[0]][0],
            type: 'error',
          });
        });
    },
    updateIngredience(ingredienceId) {
      this.formIngredience.loading = true;
      const data = Object.assign(this.formIngredience.data(), {
        recipeId: this.batch.recipe.id,
        batchId: this.batch.id,
      });
      return batchIngredienceService.update(ingredienceId, data)
        .then(() => {
          this.formIngredience.loading = false;
        })
        .catch((errors) => {
          this.formIngredience.loading = false;
          this.formIngredience.recordErrors(errors);
          const errorsKeys = Object.keys(this.formIngredience.errors.errors);
          this.$message({
            showClose: true,
            message: errors[errorsKeys[0]][0],
            type: 'error',
          });
        });
    },

    destroyIngredience(id) {
      this.$confirm(this.$t('static.recipeIngrediences.index.confirmDelete'), this.$t('static.recipeIngrediences.index.confirmDeleteTitle'), {
        confirmButtonText: this.$t('button.delete'),
        cancelButtonText: this.$t('button.cancel'),
        type: 'warning',
      }).then(() => {
        this.destroyLoading = true;
        batchIngredienceService.destroy(id)
          .then(() => {
            this.destroyLoading = false;
          }).catch(() => {
            this.destroyLoading = false;
          });
      }).catch(() => {});
    },

    updateBatch() {
      this.formBatchEdit.loading = true;
      const batch = batchTransformer.send(this.formBatchEdit.data());
      batchService
        .update(store.state.route.params.id, batch)
        .then(() => {
          this.show();
          this.formBatchEdit.loading = false;
          this.$message({
            message: this.$t('static.batches.edit.success'),
            type: 'success',
          });
        })
        .catch((errors) => {
          this.formBatchEdit.loading = false;
          this.formBatchEdit.recordErrors(errors);
        });
    },
  },

  watch: {
    allMaterials(allMaterials) {
      this.formIngredience.setOptions('materialId', allMaterials.map(c => ({
        id: c.id,
        name: `${c.name} ${c.unitMeasure !== '' ? `(${c.unitMeasure})` : ''} - ID: ${c.id}`,
      })));
      this.formIngredience.setOverrideOptions('materialId', allMaterials.map(c => ({
        id: c.id,
        name: `${c.name} ${c.unitMeasure !== '' ? `(${c.unitMeasure})` : ''} - ID: ${c.id}`,
      })));
    },
  },
  components: {
    VPagination: require('./../../../../components/paginationNew/paginationNew.vue'),
    VForm: require('./../../../../components/form/form.vue'),
    VFormTable: require('./../../../../components/formTable/formTable.vue'),
  },
};



// WEBPACK FOOTER //
// ./src/app/pages/withNavigation/batches/view/view.js